import { Injectable } from '@angular/core';
import { FormGroup, FormControl, AbstractControl } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Injectable()
export class FormsValidationService {
    public readonly ZipRegex: string = "^[0-9]{5}(?:-[0-9]{4})?$";
    public readonly ZipErrorMsg: string = "Zip must be numeric in 5 or 9 digit format 12345-1234";
    public readonly PhoneRegex: string = "^[\\+]?[(]?[0-9]{3}[)]?[-\\s\\.]?[0-9]{3}[-\\s\\.]?[0-9]{4,6}$";
    public readonly PhoneRegexMsg: string = "Phone number must be in the format (XXX) XXX-XXXX";
    public readonly EmailRegex: string = "^(\\s?[^\\s,]+@[^\\s,]+\\.[^\\s,]+\\s?,)*(\\s?[^\\s,]+@[^\\s,]+\\.[^\\s,]+)$";
    public readonly EmailRegexMsg: string = "Email must be in the format XYZ@XYZ.XYZ";
    public readonly TaxIDRegex: string = "^[1-9]\\d?-\\d{7}$";

    constructor() { }

    // get all values of the formGroup, loop over them
    // then mark each field as touched
    /**
     * Marks the entire formgroup and it's child controls as touched or dirty
     * @param formGroup The formgroup to mark
     * @param markTouched True will mark the group as touched
     * @param markDirty True will mark the group as dirty
     * @param onlyMarkReqFields True will only mark fields that have a required validator
     */
    public markFormGroup(formGroup: FormGroup, markTouched = true, markDirty = false, onlyMarkReqFields = false) {
        (<any>Object).values(formGroup.controls).forEach(control => {
            if (onlyMarkReqFields) {
                if (control.validator) {
                    let validationResult = control.validator(control);
                    if (validationResult !== null && validationResult.required === true) {
                        if (markTouched) { control.markAsTouched(); }
                        if (markDirty) { control.markAsDirty(); }
                    }
                }
            } else {
                if (markTouched) { control.markAsTouched(); }
                if (markDirty) { control.markAsDirty(); }
            }
            if (control.controls) {
                control.controls.forEach(c => this.markFormGroup(c, markTouched, markDirty, onlyMarkReqFields));
            }
        });
    }



    /**
     * Pulls data from data[field] where field is each control in the formGroup
     * This does not work with dates (unless they're strings on the form and not datepickers)
     * @param data Entity/Data to bind to the formGroup
     * @param formGroup The formGroup we want to push data into
     */
    public BindDataToFormGroup<T>(data: T, formGroup: FormGroup) {
        for (const field in formGroup.controls) { // 'field' is a string
            try {
                (<FormControl>formGroup.controls[field]).setValue(data[field]);
            } catch (ex) {
                //I have literally no recourse if this fails so log it and continue 
                console.error('Failure binding data to form', ex);
            }
        }
    }

    /**
     * Pulls data from formGroup[field] and pushes it into the corresponding data[field]
     * @param formGroup The formGroup to pull data out of
     * @param data The data object to push data into
     * @param dirtyOnly Optional parameter will only push dirty form fields if set
     */
    public BindFormGroupToData(formGroup: FormGroup, data: any, dirtyOnly: boolean = false) {
        for (const field in formGroup.controls) { // 'field' is a string
            if (dirtyOnly) {
                if ((<FormControl>formGroup.controls[field]).dirty) {
                    //Only assign if dirty
                    data[field] = (<FormControl>formGroup.controls[field]).value;
                }
            } else {
                //We don't care if it's dirty or pristine, assign
                data[field] = (<FormControl>formGroup.controls[field]).value;
            }
        }
    }

    /**
     * Returns true if the form contains no errors 
     * (if onlyCheckDirty then it will return true if only dirty fields contain no errors)
     * @param formGroup Group to validate
     * @param onlyCheckDirty Will only check for errors on dirty fields if this is true
     */
    public formIsValid(formGroup: FormGroup, onlyCheckDirty: boolean = false): boolean {

        for (const field in formGroup.controls) { // 'field' is a string
            let control = formGroup.get(field); // 'control' is a FormControl
            if (onlyCheckDirty) {
                if (control.errors && control.dirty) {
                    // console.log("ERRORS:", control.errors)
                    return false;
                }
            } else {
                if (control.errors) {
                    // console.log("ERRORS:", control.errors)
                    return false;
                }
            }
        }
        // Made it to here without returning, no errors on form
        return true;
    }


    /**
     * Updates a control within the passed in form and applies any of the other parameterized things to it
     * @param form Reference to the form containing the control to be updated
     * @param controlName The name of the control that will be updated
     * @param newVal If provided: The new value will be applied to the control using .setValue()
     * @param updateValidity If true: will call updateValueAndValidity() on the control
     * @param markTouched If true: will mark the field touched
     * @param markDirty If true: will mark the field dirty
     */
    public UpdateField(form: FormGroup, controlName: string, newVal: any = undefined, updateValidity: boolean = true, markTouched: boolean = true, markDirty: boolean = true) {
        //console.log("Updating form field: ", controlName, newVal);
        let field = form.get(controlName) as FormControl;
        if (newVal !== undefined && newVal !== null) { field.setValue(newVal); }
        if (updateValidity) { field.updateValueAndValidity(); }
        if (markTouched) { field.markAsTouched(); }
        if (markDirty) { field.markAsDirty(); }
        //console.log(form.get(controlName));
    }


    /**
     * Returns true if object is type date struct
     * @param object Datestruct
     */
    isDateStruct(object: any): object is NgbDateStruct {
        return 'year' in object;
    }

    /**
     * If control is required -> returns true
     * @param abstractControl Control to check required on
     */
    public isControlRequired(abstractControl: AbstractControl) {
        if (abstractControl.validator) {
            const validator = abstractControl.validator({} as AbstractControl);
            //console.log("validator: ", validator);
            if (validator && validator.required) {
                //console.log("has req validator");
                return true;
            }
        }
        return false;
    }
}


export const hasRequiredField = (abstractControl: AbstractControl): boolean => {
    //console.log("HASREQFIELD??");
    if (abstractControl.validator) {
        const validator = abstractControl.validator({} as AbstractControl);
        //console.log("validator: ", validator);
        if (validator && validator.required) {
            console.log("has req validator");
            return true;
        }
    }
    if (abstractControl['controls']) {
        for (const controlName in abstractControl['controls']) {
            if (abstractControl['controls'][controlName]) {
                if (hasRequiredField(abstractControl['controls'][controlName])) {
                    // console.log("Children have req validators");
                    return true;
                }
            }
        }
    }
    //console.log("NO VALIDATOR");
    return false;
};

